import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { HelpOutline } from "@mui/icons-material";
import { Bot } from "lucide-react";
import { useEffect } from "react";
import {
  AUTO_AGENT_ABILITIES,
  AUTO_AGENT_DEFAULT_MAX_STEPS,
  AUTO_AGENT_DEFAULT_MIN_RESPONSE_DELAY_MINUTES,
  AUTO_AGENT_DEFAULT_MAX_RESPONSE_DELAY_MINUTES,
} from "../../constants";

interface Agent {
  id: string;
  name: string;
  description?: string;
}

interface SequenceAutoAgentFormProps {
  actionParameters: any;
  setActionParameters: (params: any) => void;
  agents: Agent[];
  content: string;
  setContent: (content: string) => void;
}

export default function SequenceAutoAgentForm({
  actionParameters,
  setActionParameters,
  agents = [],
  content,
  setContent,
}: SequenceAutoAgentFormProps) {
  // Initialize defaults
  useEffect(() => {
    const updates: any = {};
    let hasUpdates = false;

    if (actionParameters.maxSteps === undefined) {
      updates.maxSteps = AUTO_AGENT_DEFAULT_MAX_STEPS;
      hasUpdates = true;
    }
    if (actionParameters.abilities === undefined) {
      updates.abilities = [AUTO_AGENT_ABILITIES[0].value]; // Default to first ability (email)
      hasUpdates = true;
    }
    if (actionParameters.useSenderProfileAgent === undefined) {
      updates.useSenderProfileAgent = true;
      hasUpdates = true;
    }
    if (actionParameters.minResponseDelayMinutes === undefined) {
      updates.minResponseDelayMinutes = AUTO_AGENT_DEFAULT_MIN_RESPONSE_DELAY_MINUTES;
      hasUpdates = true;
    }
    if (actionParameters.maxResponseDelayMinutes === undefined) {
      updates.maxResponseDelayMinutes = AUTO_AGENT_DEFAULT_MAX_RESPONSE_DELAY_MINUTES;
      hasUpdates = true;
    }

    if (hasUpdates) {
      setActionParameters({
        ...actionParameters,
        ...updates,
      });
    }
  }, [actionParameters, setActionParameters]);

  const handleAbilityChange = (ability: string) => {
    const currentAbilities = actionParameters.abilities || [];
    const newAbilities = currentAbilities.includes(ability)
      ? currentAbilities.filter((a: string) => a !== ability)
      : [...currentAbilities, ability];

    // Ensure at least one ability is selected
    if (newAbilities.length === 0) return;

    setActionParameters({
      ...actionParameters,
      abilities: newAbilities,
    });
  };

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 3,
          p: 2,
          bgcolor: "action.hover",
          borderRadius: 1,
        }}
      >
        <Bot size={24} />
        <Box>
          <Typography variant="subtitle1" fontWeight="medium">
            Auto Agent Loop Configuration
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure an autonomous agent to handle follow-ups until the goal is
            achieved.
          </Typography>
        </Box>
      </Box>

      {/* Goal */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="subtitle1"
          sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}
        >
          Goal
          <Tooltip title="Define what the agent should try to achieve. The agent will continue following up until this goal is met or max steps is reached.">
            <HelpOutline
              sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }}
            />
          </Tooltip>
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={3}
          placeholder="e.g., Book a meeting with the contact, Get their phone number for a demo call, Qualify whether they're a good fit..."
          value={actionParameters.goal || ""}
          onChange={(e) =>
            setActionParameters({
              ...actionParameters,
              goal: e.target.value,
            })
          }
          helperText="Be specific about what constitutes success. The agent will analyze responses to determine if the goal has been achieved."
        />
      </Box>

      {/* Agent Selection */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="subtitle1"
          sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}
        >
          Agent
          <Tooltip title="The AI agent that will handle conversations. You can use the agent from the member's sender profile (recommended) or select a specific agent.">
            <HelpOutline
              sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }}
            />
          </Tooltip>
        </Typography>

        <FormControlLabel
          control={
            <Checkbox
              checked={actionParameters.useSenderProfileAgent !== false}
              onChange={(e) =>
                setActionParameters({
                  ...actionParameters,
                  useSenderProfileAgent: e.target.checked,
                })
              }
            />
          }
          label="Use agent from sender profile (recommended)"
        />

        {!actionParameters.useSenderProfileAgent && (
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Select Agent</InputLabel>
            <Select
              label="Select Agent"
              value={actionParameters.agentId || ""}
              onChange={(e) =>
                setActionParameters({
                  ...actionParameters,
                  agentId: e.target.value,
                })
              }
            >
              {agents.map((agent) => (
                <MenuItem key={agent.id} value={agent.id}>
                  {agent.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      {/* Messaging Guidance */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="subtitle1"
          sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}
        >
          Messaging Guidance (Optional)
          <Tooltip title="Provide specific guidance for how the agent should communicate. Include tone, talking points, or constraints.">
            <HelpOutline
              sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }}
            />
          </Tooltip>
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={5}
          placeholder="e.g., Keep messages friendly and concise. Mention our free trial offer. Don't be too pushy. Focus on their pain points and how we can help..."
          value={content || ""}
          onChange={(e) => setContent(e.target.value)}
          helperText="The agent will use its persona plus this guidance when crafting messages."
        />
      </Box>

      {/* Abilities (Channels) */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="subtitle1"
          sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}
        >
          Enabled Channels
          <Tooltip title="Select which channels the agent can use for outreach. The agent will choose the most appropriate channel based on context.">
            <HelpOutline
              sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }}
            />
          </Tooltip>
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          The agent will intelligently choose the best channel for each message.
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {AUTO_AGENT_ABILITIES.map((ability) => (
            <Chip
              key={ability.value}
              label={ability.label}
              variant={
                (actionParameters.abilities || []).includes(ability.value)
                  ? "filled"
                  : "outlined"
              }
              color={
                (actionParameters.abilities || []).includes(ability.value)
                  ? "primary"
                  : "default"
              }
              onClick={() => handleAbilityChange(ability.value)}
              sx={{ cursor: "pointer" }}
            />
          ))}
        </Box>
      </Box>

      {/* Max Steps */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="subtitle1"
          sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}
        >
          Maximum Follow-ups: {actionParameters.maxSteps || AUTO_AGENT_DEFAULT_MAX_STEPS}
          <Tooltip title="Maximum number of follow-up messages the agent can send. The session ends when this limit is reached (even if the goal isn't met).">
            <HelpOutline
              sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }}
            />
          </Tooltip>
        </Typography>
        <Slider
          value={actionParameters.maxSteps || AUTO_AGENT_DEFAULT_MAX_STEPS}
          onChange={(_, value) =>
            setActionParameters({
              ...actionParameters,
              maxSteps: value as number,
            })
          }
          min={1}
          max={10}
          marks
          valueLabelDisplay="auto"
        />
      </Box>

      {/* Response Delay */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="subtitle1"
          sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}
        >
          Response Delay
          <Tooltip title="When the contact replies, how long should the agent wait before responding? A random delay within this range makes responses feel more natural and human-like. The sequence schedule is always respected—responses will only be sent during scheduled hours.">
            <HelpOutline
              sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }}
            />
          </Tooltip>
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Wait between{" "}
          <strong>
            {actionParameters.minResponseDelayMinutes ??
              AUTO_AGENT_DEFAULT_MIN_RESPONSE_DELAY_MINUTES}
          </strong>{" "}
          and{" "}
          <strong>
            {actionParameters.maxResponseDelayMinutes ||
              AUTO_AGENT_DEFAULT_MAX_RESPONSE_DELAY_MINUTES}
          </strong>{" "}
          minutes before responding to replies
        </Typography>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <TextField
            label="Min (minutes)"
            type="number"
            size="small"
            value={
              actionParameters.minResponseDelayMinutes ??
              AUTO_AGENT_DEFAULT_MIN_RESPONSE_DELAY_MINUTES
            }
            onChange={(e) => {
              const minVal = Math.max(0, parseInt(e.target.value) || 0);
              const maxVal =
                actionParameters.maxResponseDelayMinutes ??
                AUTO_AGENT_DEFAULT_MAX_RESPONSE_DELAY_MINUTES;
              setActionParameters({
                ...actionParameters,
                minResponseDelayMinutes: minVal,
                // Ensure max is at least equal to min
                maxResponseDelayMinutes: Math.max(minVal, maxVal),
              });
            }}
            inputProps={{ min: 0 }}
            sx={{ width: 120 }}
          />
          <Typography variant="body2" color="text.secondary">
            to
          </Typography>
          <TextField
            label="Max (minutes)"
            type="number"
            size="small"
            value={
              actionParameters.maxResponseDelayMinutes ??
              AUTO_AGENT_DEFAULT_MAX_RESPONSE_DELAY_MINUTES
            }
            onChange={(e) => {
              const maxVal = Math.max(0, parseInt(e.target.value) || 0);
              const minVal =
                actionParameters.minResponseDelayMinutes ??
                AUTO_AGENT_DEFAULT_MIN_RESPONSE_DELAY_MINUTES;
              setActionParameters({
                ...actionParameters,
                maxResponseDelayMinutes: maxVal,
                // Ensure min is at most equal to max
                minResponseDelayMinutes: Math.min(minVal, maxVal),
              });
            }}
            inputProps={{ min: 0 }}
            sx={{ width: 120 }}
          />
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 1 }}
        >
          Set both to 0 to respond immediately (not recommended - may appear bot-like)
        </Typography>
      </Box>

      {/* How it Works */}
      <Box
        sx={{
          p: 2,
          bgcolor: "action.hover",
          borderRadius: 1,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          <strong>How it works:</strong>
          <ul style={{ margin: "8px 0", paddingLeft: "20px" }}>
            <li>When a member reaches this step, the agent starts a session and sends the first message.</li>
            <li>When the contact replies, the agent analyzes if the goal is met. If not, it decides when and how to respond.</li>
            <li>The agent autonomously determines the best timing and channel for each follow-up based on the conversation.</li>
            <li>The session ends when: the goal is achieved, max follow-ups is reached, or the member is removed.</li>
          </ul>
        </Typography>
      </Box>
    </Box>
  );
}
